import React from 'react';
import { NumericFormat } from 'react-number-format';
import { formatPenCurrency } from '../utils/currency';

const PaymentPanel = ({
    payments,
    paymentOption,
    customerType,
    selectedPayments,
    partialPaymentAmount,
    appliedAmounts,
    onPaymentOptionChange,
    onCheckboxChange,
    onPartialAmountChange,
    onGeneralPay,
    isPreparingCheckout,
    calculateAbonoAplicado,
    calculatePendingBalanceOnlyForDisplay,
    calculateExcess,
    calculateTotalPagarComplete,
}) => (
    <div
        className="bg-gray-50 border border-gray-200 rounded-2xl p-3"
        style={{ maxHeight: '400px', overflowY: 'auto' }}
    >
        <div className="flex justify-center gap-4 mb-5">
            <button
                className={`h-12 px-8 rounded-xl font-semibold transition ${
                    paymentOption === 'complete'
                        ? 'bg-black text-white'
                        : 'bg-gray-300 text-gray-600'
                }`}
                onClick={() => onPaymentOptionChange('complete')}
            >
                Pago Completo Factura
            </button>

            <button
                className={`h-12 px-6 rounded-xl font-semibold transition ${
                    paymentOption === 'partial'
                        ? 'bg-black text-white'
                        : 'bg-gray-300 text-gray-600'
                }`}
                onClick={() => onPaymentOptionChange('partial')}
            >
                Abono
            </button>
        </div>

        {paymentOption === 'partial' && (
            <>
                <p className="text-center text-red-500 text-base mb-2">
                    Para aprovechar el beneficio del descuento por pronto pago, debe pagar el 100%
                    del valor de la factura.
                </p>

                <div className="mb-5">
                    <label className="block text-gray-800 text-base font-semibold mb-2">
                        Monto para abonar:
                    </label>

                    <NumericFormat
                        value={partialPaymentAmount}
                        onValueChange={({ value }) => onPartialAmountChange(value)}
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="S/ "
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-full text-sm outline-none focus:ring-2 focus:ring-gray-300"
                        placeholder="Ingrese el monto"
                    />
                </div>
            </>
        )}

        {payments.map((payment) => {
            const {
                id,
                invoice,
                invoiceValue,
                amount,
                discount,
                finalAmount,
                date,
                miscInfo,
            } = payment;

            const abonoReal = appliedAmounts[id] ?? 0;
            let discountToShow = discount;
            let finalAmountToShow = finalAmount;

            if (paymentOption === 'partial' && abonoReal < finalAmount) {
                discountToShow = 0;
                finalAmountToShow = amount;
            }

            let pendienteUI = finalAmountToShow - abonoReal;
            if (pendienteUI < 0) {
                pendienteUI = 0;
            }

            let completionPercent = (abonoReal / finalAmountToShow) * 100;
            if (completionPercent > 100) {
                completionPercent = 100;
            }
            completionPercent = completionPercent.toFixed(2);

            const isChecked = selectedPayments.includes(id);

            return (
                <div
                    key={id}
                    className="bg-white border border-gray-200 rounded-2xl p-3 mb-4 shadow-sm flex flex-col"
                >
                    <div className="flex items-center justify-between">
                        {paymentOption === 'partial' && customerType === 'A' ? (
                            isChecked ? (
                                <i className="fas fa-check-square text-gray-900 text-xl"></i>
                            ) : (
                                <i className="far fa-square text-gray-400 text-xl"></i>
                            )
                        ) : (
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => onCheckboxChange(id)}
                                className="h-5 w-5 rounded focus:ring-gray-400 focus:ring-2"
                            />
                        )}

                        <div className="ml-3 flex-1">
                            <p className="text-gray-900 font-semibold text-sm">
                                {miscInfo === 'DB' ? 'Nota Debito' : 'Factura'}: {invoice}
                            </p>
                            <p className="text-gray-600 text-sm">
                                Valor Factura: {formatPenCurrency(invoiceValue)}
                            </p>
                            <p className="text-gray-600 text-sm">
                                Saldo Factura: {formatPenCurrency(amount)}
                            </p>
                            <p className="text-gray-600 text-sm">
                                Descuento por Pronto Pago: {formatPenCurrency(discountToShow)}
                            </p>
                            <p className="text-gray-600 text-sm">
                                Valor Total con Descuento: {formatPenCurrency(finalAmountToShow)}
                            </p>
                            <p className="text-gray-600 text-sm">
                                Fecha de Emisión: {new Date(date).toLocaleDateString()}
                            </p>
                        </div>
                    </div>

                    {paymentOption === 'partial' && isChecked && partialPaymentAmount && (
                        <div className="mt-3 text-right flex flex-col items-center space-y-2">
                            <div>
                                <label className="block text-gray-600 text-sm">Saldo Pendiente:</label>
                                <input
                                    type="text"
                                    value={formatPenCurrency(pendienteUI)}
                                    readOnly
                                    className="w-28 px-3 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm"
                                />
                            </div>
                            <div
                                className={`text-xs font-semibold ${
                                    completionPercent === '100.00'
                                        ? 'text-green-600'
                                        : 'text-red-500'
                                }`}
                            >
                                {`Cumplimiento: ${completionPercent}%`}
                            </div>
                        </div>
                    )}
                </div>
            );
        })}

        {paymentOption === 'partial' && (
            <div className="mt-4 max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <span className="text-sm font-bold text-gray-900">Monto Abonado:</span>
                    <span className="text-sm font-bold text-gray-900 text-right">
                        {formatPenCurrency(calculateAbonoAplicado())}
                    </span>

                    <span className="text-sm font-bold text-gray-900">Saldo Pendiente:</span>
                    <span className="text-sm font-bold text-gray-900 text-right">
                        {formatPenCurrency(calculatePendingBalanceOnlyForDisplay())}
                    </span>

                    {calculateExcess() > 0 && (
                        <>
                            <span className="text-sm font-bold text-gray-900">Saldo a Favor:</span>
                            <span className="text-sm font-bold text-gray-900 text-right">
                                {formatPenCurrency(calculateExcess())}
                            </span>
                        </>
                    )}
                </div>
            </div>
        )}

        {paymentOption === 'complete' && (
            <div className="mt-2 text-sm font-bold text-gray-900 text-center">
                Total a Pagar: {formatPenCurrency(calculateTotalPagarComplete())}
            </div>
        )}

        <button
            onClick={onGeneralPay}
            className="w-full bg-gray-900 text-white py-3 rounded-full font-medium hover:bg-gray-800 transition text-sm mt-5 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={selectedPayments.length === 0 || isPreparingCheckout}
        >
            {isPreparingCheckout ? 'Preparando Checkout Pro...' : 'Pagar en Mercado Pago'}
        </button>
    </div>
);

export default PaymentPanel;
